import React, { useState, useMemo } from 'react';
import { HelpCircle, Plus, Upload, Sparkles, Mail, ChevronLeft, ChevronRight, Copy } from 'lucide-react';
import { getSessionStatus } from './CompletedInterviews';
import toast from 'react-hot-toast';

interface CandidatesInviteSectionProps {
  assessment: any;
  existingCandidates: any[];
  isCandidatesExpanded: boolean;
  setIsCandidatesExpanded: (expanded: boolean) => void;
  candidateRows: Array<{ firstName: string; lastName: string; email: string; phone: string }>;
  candidateErrors: Array<{ firstName?: string; lastName?: string; email?: string; phone?: string }>;
  handleCandidateInputChange: (index: number, field: string, value: string) => void;
  handleAddCandidateRow: () => void;
  handleSendInvitations: () => void;
  candidatesLoading?: boolean;
  candidatesPage?: number;
  candidatesTotal?: number;
  candidatesItemsPerPage?: number;
  onPageChange?: (page: number) => void;
}

const CandidatesInviteSection: React.FC<CandidatesInviteSectionProps> = ({
  assessment,
  existingCandidates,
  isCandidatesExpanded,
  setIsCandidatesExpanded,
  candidateRows,
  candidateErrors,
  handleCandidateInputChange,
  handleAddCandidateRow,
  handleSendInvitations,
  candidatesLoading = false,
  candidatesPage = 1,
  candidatesTotal = 0,
  candidatesItemsPerPage = 10,
  onPageChange,
}) => {
  const [showInvitationPreview, setShowInvitationPreview] = useState(false);
  const [invitationMailContent, setInvitationMailContent] = useState(`Subject: Interview Invitation

Dear Candidate,

We are pleased to invite you to participate in our interview assessment.

Please click on the link below to begin your interview:
[Interview Link]

Best regards,
EarlyJobs Team`);

  // Calculate pagination from API response
  const totalPages = Math.ceil(candidatesTotal / candidatesItemsPerPage);
  const startIndex = (candidatesPage - 1) * candidatesItemsPerPage;
  const endIndex = Math.min(startIndex + candidatesItemsPerPage, candidatesTotal);

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages && onPageChange) {
      onPageChange(page);
    }
  };

  return (
    <>
      <style>{`
        @keyframes shimmer {
          0% {
            background-position: -200% 0;
          }
          100% {
            background-position: 200% 0;
          }
        }
        .shimmer {
          background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
          background-size: 200% 100%;
          animation: shimmer 1.5s infinite;
        }
      `}</style>
      <div className="max-w-4xl">
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center space-x-2 mb-1">
            <h2 className="text-lg font-semibold text-gray-900">Invite Candidates</h2>
            <HelpCircle className="h-4 w-4 text-blue-500" />
          </div>
          {assessment.status === 'draft' && <p className="flex items-center gap-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 px-4 py-1 my-3 rounded-md">
            <span>⚠️</span>
            Assessment is still in draft mode. Please complete all skills and settings before inviting candidates.
          </p>}
          {/* Existing Candidates Section with Pagination */}
          {(existingCandidates.length > 0 || candidatesLoading) && (
            <div className="mb-8">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base font-medium text-gray-900">
                {candidatesLoading ? (
                  <span className="inline-block h-5 w-48 bg-gray-200 rounded animate-pulse"></span>
                ) : (
                  `Added Candidates (${candidatesTotal})`
                )}
              </h3>
            </div>
            <div className="overflow-x-auto border border-gray-200 rounded-lg">
              <table className="w-full min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-700 uppercase tracking-wider">First Name</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-700 uppercase tracking-wider">Last Name</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-700 uppercase tracking-wider">Email</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-700 uppercase tracking-wider">Phone</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-700 uppercase tracking-wider">Status</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {candidatesLoading ? (
                    // Shimmer loading rows
                    Array.from({ length: 10 }).map((_, index) => (
                      <tr key={`shimmer-${index}`}>
                        <td className="py-3 px-4">
                          <div className="h-4 rounded w-24 shimmer"></div>
                        </td>
                        <td className="py-3 px-4">
                          <div className="h-4 rounded w-24 shimmer"></div>
                        </td>
                        <td className="py-3 px-4">
                          <div className="h-4 rounded w-40 shimmer"></div>
                        </td>
                        <td className="py-3 px-4">
                          <div className="h-4 rounded w-32 shimmer"></div>
                        </td>
                        <td className="py-3 px-4">
                          <div className="h-6 rounded-full w-20 shimmer"></div>
                        </td>
                      </tr>
                    ))
                  ) : existingCandidates.length > 0 ? (
                    existingCandidates.map((candidate, index) => (
                      <tr key={candidate._id || index} className="hover:bg-gray-50 transition-colors">
                        <td className="py-3 px-4 text-sm text-gray-900 whitespace-nowrap">{candidate.firstName || '-'}</td>
                        <td className="py-3 px-4 text-sm text-gray-900 whitespace-nowrap">{candidate.lastName || '-'}</td>
                        <td className="py-3 px-4 text-sm text-gray-900 whitespace-nowrap">{candidate.email || '-'}</td>
                        <td className="py-3 px-4 text-sm text-gray-900 whitespace-nowrap">{candidate.phone || '-'}</td>
                        <td className="py-3 px-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            {getSessionStatus(candidate.status)}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                // Construct interview URL
                                const frontendUrl = process.env.REACT_APP_FRONTEND_URL || window.location.origin;
                                const assessmentId = assessment?._id || assessment?.id;
                                const candidateId = candidate._id || candidate.candidateId;
                                const sessionId = candidate.sessionId;
                                
                                if (!assessmentId || !candidateId || !sessionId) {
                                  toast.error('Interview URL not available');
                                  return;
                                }
                                
                                const interviewUrl = `${frontendUrl}/interview?sessionId=${sessionId}`;
                                
                                navigator.clipboard.writeText(interviewUrl).then(() => {
                                  toast.success('Interview URL copied to clipboard');
                                }).catch(() => {
                                  toast.error('Failed to copy URL');
                                });
                              }}
                              className="text-gray-500 hover:text-gray-700 transition-colors p-1 rounded hover:bg-gray-100"
                              title="Copy interview URL"
                            >
                              <Copy className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={5} className="py-8 px-4 text-center text-sm text-gray-500">
                        No candidates found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls */}
            {!candidatesLoading && totalPages > 1 && (
              <div className="mt-4 flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  Showing <span className="font-medium">{startIndex + 1}</span> to{' '}
                  <span className="font-medium">{endIndex}</span> of{' '}
                  <span className="font-medium">{candidatesTotal}</span> candidates
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handlePageChange(candidatesPage - 1)}
                    disabled={candidatesPage === 1}
                    className={`px-3 py-2 text-sm font-medium rounded-lg border transition-colors ${
                      candidatesPage === 1
                        ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    Previous
                  </button>
                  
                  {/* Page Numbers */}
                  <div className="flex items-center space-x-1">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                      // Show first page, last page, current page, and pages around current
                      if (
                        page === 1 ||
                        page === totalPages ||
                        (page >= candidatesPage - 1 && page <= candidatesPage + 1)
                      ) {
                        return (
                          <button
                            key={page}
                            onClick={() => handlePageChange(page)}
                            className={`px-3 py-2 text-sm font-medium rounded-lg border transition-colors ${
                              candidatesPage === page
                                ? 'bg-blue-600 text-white border-blue-600'
                                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                            }`}
                          >
                            {page}
                          </button>
                        );
                      } else if (page === candidatesPage - 2 || page === candidatesPage + 2) {
                        return (
                          <span key={page} className="px-2 text-gray-500">
                            ...
                          </span>
                        );
                      }
                      return null;
                    })}
                  </div>

                  <button
                    onClick={() => handlePageChange(candidatesPage + 1)}
                    disabled={candidatesPage === totalPages}
                    className={`px-3 py-2 text-sm font-medium rounded-lg border transition-colors ${
                      candidatesPage === totalPages
                        ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* By email Section */}
        <div className="mb-8">
          <h3 className="text-base font-medium text-gray-900 mb-4">By email</h3>
          <div className="space-y-4 mb-4">
            {candidateRows.map((row, index) => {
              const errors = candidateErrors[index] || {};
              return (
                <div key={index} className="space-y-2">
                  <div className="grid grid-cols-4 gap-3">
                    <div>
                      <input
                        type="text"
                        placeholder="First name *"
                        value={row.firstName}
                        onChange={(e) => handleCandidateInputChange(index, 'firstName', e.target.value)}
                        required
                        className={`px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-sm w-full ${
                          errors.firstName ? 'border-red-500' : 'border-gray-300'
                        }`}
                      />
                      {errors.firstName && (
                        <p className="text-xs text-red-600 mt-1">{errors.firstName}</p>
                      )}
                    </div>
                    <div>
                      <input
                        type="text"
                        placeholder="Last name *"
                        value={row.lastName}
                        onChange={(e) => handleCandidateInputChange(index, 'lastName', e.target.value)}
                        required
                        className={`px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-sm w-full ${
                          errors.lastName ? 'border-red-500' : 'border-gray-300'
                        }`}
                      />
                      {errors.lastName && (
                        <p className="text-xs text-red-600 mt-1">{errors.lastName}</p>
                      )}
                    </div>
                    <div>
                      <input
                        type="email"
                        placeholder="Email *"
                        value={row.email}
                        onChange={(e) => handleCandidateInputChange(index, 'email', e.target.value)}
                        required
                        className={`px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-sm w-full ${
                          errors.email ? 'border-red-500' : 'border-gray-300'
                        }`}
                      />
                      {errors.email && (
                        <p className="text-xs text-red-600 mt-1">{errors.email}</p>
                      )}
                    </div>
                    <div>
                      <input
                        type="tel"
                        placeholder="Phone *"
                        value={row.phone}
                        onChange={(e) => handleCandidateInputChange(index, 'phone', e.target.value)}
                        required
                        className={`px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-sm w-full ${
                          errors.phone ? 'border-red-500' : 'border-gray-300'
                        }`}
                      />
                      {errors.phone && (
                        <p className="text-xs text-red-600 mt-1">{errors.phone}</p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="flex items-center space-x-4 mb-4">
            {assessment.status === 'active' && <button
              onClick={handleAddCandidateRow}
              className="flex items-center space-x-1 text-sm text-blue-600 hover:text-blue-700 font-medium"
            >
              <Plus className="h-4 w-4" />
              <span>Add more rows</span>
            </button>}
            <button className="flex items-center space-x-1 text-sm text-gray-700 hover:text-gray-900 font-medium">
              <Upload className="h-4 w-4" />
              <span>Upload CSV</span>
            </button>
            <button className="flex items-center space-x-1 text-sm text-gray-700 hover:text-gray-900 font-medium">
              <Sparkles className="h-4 w-4" />
              <span>Grade Resume with AI</span>
            </button>
            <button 
              onClick={() => setShowInvitationPreview(!showInvitationPreview)}
              className="flex items-center space-x-1 text-sm text-gray-700 hover:text-gray-900 font-medium"
            >
              <Mail className="h-4 w-4" />
              <span>Preview/Edit invitation mail</span>
            </button>
          </div>
          <button disabled={assessment.status === 'draft'} className={`px-6 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors ${assessment.status === 'draft' ? 'bg-gray-400 text-gray-200 cursor-not-allowed hover:bg-gray-400' : ''}`} onClick={handleSendInvitations}>
            Send Invitations
          </button>
        </div>

        {/* Invitation Mail Preview */}
        {showInvitationPreview && (
          <div className="border-t border-gray-200 pt-6 mt-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-medium text-gray-900">Invitation Mail Preview</h3>
              <button
                onClick={() => setShowInvitationPreview(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ×
              </button>
            </div>
            <textarea
              value={invitationMailContent}
              onChange={(e) => setInvitationMailContent(e.target.value)}
              rows={20}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none font-mono text-sm"
              placeholder="Enter invitation mail content..."
            />
            <p className="text-xs text-gray-500 mt-2">
              This is how your invitation email will look. You can edit the content above.
            </p>
          </div>
        )}

        {/* By public link Section */}
        <div className="border-t border-gray-200 pt-6">
          <div className="flex items-center space-x-2 mb-3">
            <h3 className="text-base font-medium text-gray-900">By public link</h3>
            <HelpCircle className="h-4 w-4 text-blue-500" />
          </div>
          <p className="text-sm text-gray-600 mb-4">
            Invite candidates via link through social network or your own careers page using trackable and customized links. Anyone with the link is able to take the assessment.
          </p>
          <button className="flex items-center space-x-1 text-sm text-blue-600 hover:text-blue-700 font-medium">
            <Plus className="h-4 w-4" />
            <span>Add trackable link</span>
          </button>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-end space-x-3 mt-6">
        <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
          <ChevronLeft className="h-5 w-5 text-gray-600" />
        </button>
        <button className="px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2">
          <span>Next</span>
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
      </div>
    </>
  );
};

export default CandidatesInviteSection;
